import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import RightSlideModal from '../../components/RightSlideModal';
import CommonTable, { TableRowAvatarCell } from '../../components/CommonTable';

export default function TeachersPage() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', qualification: '', subjects: [], assignedClasses: [] });

  useEffect(() => {
    fetchTeachers();
  }, []);

  const fetchTeachers = async () => {
    try {
      const response = await api.get('/teachers');
      setTeachers(response.data);
    } catch (error) {
      toast.error('Failed to fetch teachers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedTeacher) {
        await api.put(`/teachers/${selectedTeacher.id}`, formData);
        toast.success('Teacher updated successfully');
      } else {
        await api.post('/teachers', formData);
        toast.success('Teacher added successfully (Default password: teacher123)');
      }
      setShowModal(false);
      setFormData({ name: '', email: '', phone: '', qualification: '', subjects: [], assignedClasses: [] });
      setSelectedTeacher(null);
      fetchTeachers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save teacher');
    }
  };

  const handleDelete = async (teacherId) => {
    if (!window.confirm('Are you sure you want to delete this teacher?')) return;
    try {
      await api.delete(`/teachers/${teacherId}`);
      toast.success('Teacher deleted successfully');
      fetchTeachers();
    } catch (error) {
      toast.error('Failed to delete teacher');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-outfit font-semibold text-2xl text-slate-800">Teacher Management</h2>
          <p className="text-slate-500">Manage teaching staff</p>
        </div>
        <button
          data-testid="add-teacher-button"
          onClick={() => setShowModal(true)}
          className="bg-green-600 hover:bg-green-700 text-white rounded-full px-6 py-2.5 font-medium shadow-sm active:scale-95 transition-all flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add Teacher
        </button>
      </div>

      <CommonTable headers={['Teacher', 'Qualification', 'Email', 'Phone', 'Credentials', 'Actions']}>
        {teachers.length > 0 ? teachers.map((teacher, index) => {
          const colors = [
            'bg-amber-600/90 text-white',
            'bg-teal-600/90 text-white',
            'bg-indigo-600/90 text-white',
            'bg-pink-600/90 text-white',
            'bg-rose-600/90 text-white',
            'bg-cyan-600/90 text-white',
            'bg-emerald-600/90 text-white'
          ];
          const colorClass = colors[index % colors.length];
          return (
            <tr key={teacher.id} className="hover:bg-slate-50/50 transition-colors">
              <td className="px-6 py-4">
                <TableRowAvatarCell 
                  title={teacher.name} 
                  subtitle={teacher.qualification || 'Teacher'}
                  colorClass={colorClass}
                />
              </td>
              <td className="px-6 py-4 text-slate-600 font-medium text-sm">
                {teacher.qualification || 'N/A'}
              </td>
              <td className="px-6 py-4 text-slate-500 text-sm">
                {teacher.email}
              </td>
              <td className="px-6 py-4 text-slate-500 text-sm">
                {teacher.phone || 'N/A'}
              </td>
              <td className="px-6 py-4">
                <div className="flex flex-col gap-0.5 text-xs">
                  <span className="text-slate-400">ID: {teacher.email}</span>
                  <span className="font-mono bg-green-50 text-green-700 px-2 py-0.5 rounded font-bold self-start mt-1 select-all">teacher123</span>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center gap-2">
                  <button onClick={() => { setSelectedTeacher(teacher); setFormData({ name: teacher.name, email: teacher.email, phone: teacher.phone, qualification: teacher.qualification, subjects: teacher.subjects, assignedClasses: teacher.assignedClasses }); setShowModal(true); }} className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(teacher.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          );
        }) : (
          <tr>
            <td colSpan="6" className="text-center py-12 text-slate-500">No teachers found</td>
          </tr>
        )}
      </CommonTable>

      <RightSlideModal 
        isOpen={showModal} 
        onClose={() => setShowModal(false)} 
        title={selectedTeacher ? 'Edit Teacher' : 'Add New Teacher'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
            <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-green-100 focus:border-green-500 transition-all outline-none" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
            <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-green-100 focus:border-green-500 transition-all outline-none" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
            <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-green-100 focus:border-green-500 transition-all outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Qualification</label>
            <input type="text" value={formData.qualification} onChange={(e) => setFormData({ ...formData, qualification: e.target.value })} className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-green-100 focus:border-green-500 transition-all outline-none" placeholder="e.g., M.Ed, B.Ed" />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="submit" className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-full py-2.5 font-medium">{selectedTeacher ? 'Update' : 'Add Teacher'}</button>
            <button type="button" onClick={() => setShowModal(false)} className="flex-1 border border-slate-200 hover:bg-slate-50 rounded-full py-2.5 font-medium">Cancel</button>
          </div>
        </form>
      </RightSlideModal>
    </div>
  );
}
